import React, { useState, useEffect } from 'react';
import authenticationService from '../../services/authenticationService';

const MfaPolicy = ({ scopeType, scopeId }) => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadPolicy();
  }, [scopeType, scopeId]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      const data = await authenticationService.getPolicy(scopeType, scopeId);
      setPolicy(data.mfaPolicy || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await authenticationService.updateMfaPolicy(scopeType, scopeId, policy);
      setSuccess('MFA policy updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  if (!policy) {
    return <div>Failed to load MFA policy</div>;
  }

  return (
    <div>
      {/* Error/Success messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '8px',
          color: '#065f46',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Policy Form */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
          Multi-Factor Authentication Policy
        </h3>

        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Enforcement Level */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              MFA Enforcement Level
            </label>
            <select
              value={policy.enforcementLevel || 'None'}
              onChange={(e) => setPolicy({ ...policy, enforcementLevel: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff'
              }}
            >
              <option value="None">None - MFA is optional</option>
              <option value="Optional">Optional - Users can enable MFA</option>
              <option value="Required">Required - All users must enable MFA</option>
              <option value="AdminsOnly">Admins Only - Only admins must enable MFA</option>
              <option value="RoleBased">Role Based - Based on user roles</option>
            </select>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {policy.enforcementLevel === 'Required' && 'All users will be required to set up MFA'}
              {policy.enforcementLevel === 'AdminsOnly' && 'Only administrators will be required to set up MFA'}
              {policy.enforcementLevel === 'Optional' && 'Users can optionally enable MFA for additional security'}
              {policy.enforcementLevel === 'None' && 'MFA is disabled'}
            </p>
          </div>

          {/* Grace Period */}
          {policy.enforcementLevel !== 'None' && policy.enforcementLevel !== 'Optional' && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Grace Period (days)
              </label>
              <input
                type="number"
                min={0}
                max={90}
                value={policy.gracePeriodDays || 7}
                onChange={(e) => setPolicy({ ...policy, gracePeriodDays: parseInt(e.target.value) || 7 })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Users have {policy.gracePeriodDays || 7} days to set up MFA before enforcement begins
              </p>
            </div>
          )}

          {/* Supported Methods */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
              Supported MFA Methods
            </label>
            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.smsEnabled !== false}
                  onChange={(e) => setPolicy({ ...policy, smsEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>SMS (Text Message)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.authenticatorAppEnabled !== false}
                  onChange={(e) => setPolicy({ ...policy, authenticatorAppEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Authenticator App (TOTP - Google Authenticator, Authy, etc.)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.hardwareKeyEnabled !== false}
                  onChange={(e) => setPolicy({ ...policy, hardwareKeyEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Hardware Keys (FIDO2/WebAuthn - YubiKey, etc.)</span>
              </label>
            </div>
          </div>

          {/* Remember Device */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Remember Device (days)
            </label>
            <input
              type="number"
              min={0}
              max={365}
              value={policy.rememberDeviceDays || 30}
              onChange={(e) => setPolicy({ ...policy, rememberDeviceDays: parseInt(e.target.value) || 30 })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {policy.rememberDeviceDays === 0 
                ? 'Users must enter MFA code every time' 
                : `Trusted devices skip MFA for ${policy.rememberDeviceDays || 30} days`}
            </p>
          </div>

          {/* Bypass IP Ranges */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Bypass IP Ranges (JSON array)
            </label>
            <textarea
              value={policy.bypassIpRanges || ''}
              onChange={(e) => setPolicy({ ...policy, bypassIpRanges: e.target.value })}
              placeholder='["192.168.1.0/24", "10.0.0.0/8"]'
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              IP ranges where MFA is not required (office networks). Format: JSON array of CIDR notation.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 24px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MfaPolicy;

